import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useRequiredContext } from "@redotech/react-util/context";
import UserIcon from "@redotech/redo-icons/user-01.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { GetUser } from "@redotech/redo-model/user";
import { RedoAvatar } from "@redotech/redo-web/arbiter-components/avatar/redo-avatar";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useMemo, type MouseEvent } from "react";
import type { SupportTicketPresenceRecord } from "../db/collections";
import { useSupportTicketCard } from "../hooks/use-support-ticket-card";
import { useSupportTicketPresence } from "../hooks/use-support-ticket-data";
import { PriorityBadge, StatusBadge } from "./badges/support-ticket-badges";
import * as styles from "./support-ticket-card.module.css";

export interface SupportTicketCardProps {
  supportTicket: SupportTicketListItemResponse;
  isActive: boolean;
  onClick: (event: MouseEvent<HTMLDivElement>) => void;
}

export const SupportTicketCard = memo(function SupportTicketCard({
  supportTicket,
  isActive,
  onClick,
}: SupportTicketCardProps) {
  const { isUnread, timeSinceMessage, lastMessageDate } = useSupportTicketCard({
    supportTicket,
  });
  const { data: presenceUsers } = useSupportTicketPresence(
    supportTicket.id as SupportTicketId,
  );
  const team = useRequiredContext(TeamContext);
  const userMap = useMemo(
    () =>
      new Map(
        team.users.map((u) => {
          const user = u.user as GetUser;
          return [user._id, user.name];
        }),
      ),
    [team.users],
  );

  return (
    <Flex
      bgColor={isActive ? "quaternary" : "primary"}
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      className={styles.card}
      cursor="pointer"
      hoverColor={isActive ? undefined : "primary"}
      onClick={onClick}
      p="lg"
    >
      <Flex dir="column" gap="sm" overflow="hidden" w="full">
        {/* Row 1: Header - Name/time column + unread dot */}
        <Flex align="flex-start" justify="space-between">
          <Flex dir="column" gap="none">
            <Text
              fontSize="xs"
              fontWeight={isUnread ? "semibold" : "regular"}
              textColor="primary"
            >
              {supportTicket.customer?.displayName ?? "Customer"}
            </Text>
            {lastMessageDate && (
              <Tooltip arrow title={lastMessageDate.toLocaleString()}>
                <span>
                  <Text fontSize="xxs" textColor="tertiary">
                    {timeSinceMessage}
                  </Text>
                </span>
              </Tooltip>
            )}
          </Flex>
          {isUnread && <div className={styles.unreadDot} />}
        </Flex>

        {/* Row 2: Subject + Preview */}
        <Flex dir="column" gap="none">
          {supportTicket.summary && (
            <Text className={styles.summary} fontSize="xs" textColor="tertiary">
              {supportTicket.summary}
            </Text>
          )}
        </Flex>

        {/* Row 3: Badges + Presence avatars */}
        <Flex align="center" justify="space-between">
          <Flex align="center" gap="xs">
            <StatusBadge size="sm" status={supportTicket.status} />
            {supportTicket.priority === "URGENT" && (
              <PriorityBadge priority={supportTicket.priority} size="sm" />
            )}
          </Flex>
          {presenceUsers.length > 0 && (
            <PresenceAvatarGroup userMap={userMap} users={presenceUsers} />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

const MAX_VISIBLE_AVATARS = 3;

const PresenceAvatarGroup = memo(function PresenceAvatarGroup({
  users,
  userMap,
}: {
  users: SupportTicketPresenceRecord[];
  userMap: Map<string, string>;
}) {
  const overlap = 16;
  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => {
        if (a.isTyping !== b.isTyping) {
          return a.isTyping ? -1 : 1;
        }
        return a.userId.localeCompare(b.userId);
      }),
    [users],
  );
  const visible = sortedUsers.slice(0, MAX_VISIBLE_AVATARS);
  const overflow = users.length - visible.length;

  return (
    <Flex align="center">
      <Flex position="relative">
        {visible.map((user, index) => {
          const name = userMap.get(user.userId);
          const label = user.isTyping
            ? `${name ?? user.userId} is typing…`
            : `${name ?? user.userId} is viewing`;

          return (
            <Tooltip arrow key={user.id} title={label}>
              <div
                style={{
                  marginLeft: index === 0 ? 0 : -overlap,
                  position: "relative",
                  zIndex: MAX_VISIBLE_AVATARS - index,
                }}
              >
                <RedoAvatar
                  content={
                    name
                      ? { type: "text", text: name }
                      : { type: "icon", Icon: UserIcon }
                  }
                  size="xs"
                  status={user.isTyping ? { type: "typing" } : undefined}
                />
              </div>
            </Tooltip>
          );
        })}
      </Flex>
      {overflow > 0 && (
        <Text fontSize="xxs" ml="xs" textColor="tertiary">
          +{overflow}
        </Text>
      )}
    </Flex>
  );
});

export interface SupportTicketCardSkeletonProps {
  count?: number;
}

export const SupportTicketCardSkeleton = memo(
  function SupportTicketCardSkeleton({
    count = 3,
  }: SupportTicketCardSkeletonProps) {
    return (
      <>
        {Array.from({ length: count }).map((_, index) => (
          <Flex
            borderBottomWidth="1px"
            borderColor="secondary"
            borderStyle="solid"
            key={index}
            p="lg"
          >
            <Flex dir="column" gap="sm">
              <Flex align="center" gap="sm" justify="space-between">
                <div
                  style={{
                    width: 100,
                    height: 14,
                    backgroundColor:
                      "var(--redo-colors-background-bg-tertiary)",
                    borderRadius: 4,
                  }}
                />
                <div
                  style={{
                    width: 40,
                    height: 12,
                    backgroundColor:
                      "var(--redo-colors-background-bg-tertiary)",
                    borderRadius: 4,
                  }}
                />
              </Flex>
              <div
                style={{
                  width: "100%",
                  height: 28,
                  backgroundColor: "var(--redo-colors-background-bg-tertiary)",
                  borderRadius: 4,
                }}
              />
              <Flex align="center" gap="xs">
                <div
                  style={{
                    width: 50,
                    height: 20,
                    backgroundColor:
                      "var(--redo-colors-background-bg-tertiary)",
                    borderRadius: 10,
                  }}
                />
              </Flex>
            </Flex>
          </Flex>
        ))}
      </>
    );
  },
);

export const SupportTicketCardEmpty = memo(function SupportTicketCardEmpty() {
  return (
    <Flex align="center" justify="center" p="xl">
      <Text fontSize="xs" textColor="tertiary">
        No support tickets
      </Text>
    </Flex>
  );
});
